/**
 * Media Tracker logic. PURE — no React, no Date-of-today lookups.
 *
 * `todayKey` is always passed in by the caller (from `useLocalDateKey()`), so
 * every transition is reproducible in a test and the panel re-derives on the
 * midnight rollover rather than at mount time only.
 *
 * THE RULE THIS FILE EXISTS TO ENFORCE:
 *   `new Date(releaseDate)` is NEVER called. A bare 'YYYY-MM-DD' goes through
 *   `new Date()` as UTC midnight, i.e. the previous local day west of
 *   Greenwich, so every item would go bold and then struck a day early.
 *   Date keys are zero-padded and fixed-width, so a string compare IS the
 *   calendar compare. `parseLocalDateKey` (in src/lib/date.ts) is the only
 *   sanctioned parse, and it is reached only via daysBetweenKeys below.
 */
import { compareDateKeys, daysBetweenKeys, isValidDateKey, relativeDayLabel } from '../date';
import {
  asRecord,
  asTrimmedString,
  EM_DASH,
  MEDIA_TYPES,
  type MediaItem,
  type MediaType,
  type ReleaseStatus,
} from './types';

export const MEDIA_TYPE_LABELS: Readonly<Record<MediaType, string>> = {
  game: 'Game',
  anime: 'Anime',
  movie: 'Movie',
  other: 'Other',
};

export function isMediaType(value: unknown): value is MediaType {
  return typeof value === 'string' && (MEDIA_TYPES as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ */
/* Status                                                              */
/* ------------------------------------------------------------------ */

/**
 * The graded truth table.
 *
 *   release === today  -> 'released-today'  bold, accent, no strike, "TODAY"
 *   release  <  today  -> 'past'            line-through opacity-60, "N days ago"
 *   release  >  today  -> 'upcoming'        normal weight, "in N days"
 *   missing/malformed  -> 'invalid'         renders normally, EM_DASH date
 *
 * `release` is deliberately typed `unknown`: it comes out of localStorage,
 * where it may be null, a number, or 'next tuesday'. Nothing in here can
 * throw, and nothing can produce NaN or 'Invalid Date'.
 *
 * NOTE: 'invalid' and 'past' are *rendering* states, never deletion states.
 * No caller may filter on this value — items are removed only by the user.
 */
export function releaseStatus(release: unknown, todayKey: string): ReleaseStatus {
  // A malformed *today* would be a bug in the shared clock, but treating it
  // as invalid keeps the panel rendering instead of mis-classifying every row.
  if (!isValidDateKey(release) || !isValidDateKey(todayKey)) return 'invalid';
  const cmp = compareDateKeys(release, todayKey);
  if (cmp === 0) return 'released-today';
  return cmp < 0 ? 'past' : 'upcoming';
}

/** The date-column text. 'TODAY' / 'in 3 days' / '2 days ago' / EM_DASH. */
export function releaseLabel(release: unknown, todayKey: string): string {
  if (!isValidDateKey(release) || !isValidDateKey(todayKey)) return EM_DASH;
  const days = daysBetweenKeys(todayKey, release);
  if (days === null) return EM_DASH;
  return days === 0 ? 'TODAY' : relativeDayLabel(days);
}

export interface ReleaseView {
  readonly status: ReleaseStatus;
  readonly label: string;
  /** Whole local days from today to the release. null when invalid. */
  readonly days: number | null;
}

/** Status + label in one pass, so a row derives everything once. */
export function describeRelease(release: unknown, todayKey: string): ReleaseView {
  const status = releaseStatus(release, todayKey);
  if (status === 'invalid') return { status, label: EM_DASH, days: null };
  const days = daysBetweenKeys(todayKey, release as string);
  return {
    status,
    label: days === null ? EM_DASH : days === 0 ? 'TODAY' : relativeDayLabel(days),
    days,
  };
}

/* ------------------------------------------------------------------ */
/* Ordering                                                            */
/* ------------------------------------------------------------------ */

/** Group order: today first, then what's coming, then what's gone, then undated. */
const GROUP_RANK: Readonly<Record<ReleaseStatus, number>> = {
  'released-today': 0,
  upcoming: 1,
  past: 2,
  invalid: 3,
};

/**
 * Sorts a COPY. Never filters — a past or invalid item keeps its row forever
 * until the user deletes it. Soonest upcoming first; most recent past first.
 */
export function sortMediaItems(
  items: readonly MediaItem[],
  todayKey: string,
): readonly MediaItem[] {
  return [...items].sort((a, b) => {
    const sa = releaseStatus(a.releaseDate, todayKey);
    const sb = releaseStatus(b.releaseDate, todayKey);
    if (sa !== sb) return GROUP_RANK[sa] - GROUP_RANK[sb];
    if (sa === 'invalid') return a.title.localeCompare(b.title);
    const byDate = compareDateKeys(a.releaseDate, b.releaseDate);
    if (byDate !== 0) return sa === 'past' ? -byDate : byDate;
    return a.title.localeCompare(b.title);
  });
}

/* ------------------------------------------------------------------ */
/* Persistence guard                                                   */
/* ------------------------------------------------------------------ */

/**
 * Salvaging migration for the `my-hub:media:items` blob. PURE.
 *
 * Total garbage (a string, an object, null) yields []. A partially-bad array
 * keeps every row it can rather than discarding the user's whole library for
 * one bad neighbour. An unrecognised `type` degrades to 'other'; a malformed
 * `releaseDate` is KEPT VERBATIM, because the truth table requires a row with
 * a bad date to still render (with EM_DASH) and still be deletable.
 */
export function migrateMedia(raw: unknown): readonly MediaItem[] {
  if (!Array.isArray(raw)) return [];
  const out: MediaItem[] = [];
  const seen = new Set<string>();
  raw.forEach((row, index) => {
    const item = toMediaItem(row, index);
    if (item === null || seen.has(item.id)) return;
    seen.add(item.id);
    out.push(item);
  });
  return out;
}

function toMediaItem(row: unknown, index: number): MediaItem | null {
  const r = asRecord(row);
  if (r === null) return null;
  const title = asTrimmedString(r.title);
  if (title === '') return null; // a title-less row is not renderable or addressable
  const id = asTrimmedString(r.id) || `media-legacy-${index}`;
  return {
    id,
    title,
    // Kept as stored. Only strings survive; anything else becomes '' -> invalid.
    releaseDate: typeof r.releaseDate === 'string' ? r.releaseDate.trim() : '',
    type: isMediaType(r.type) ? r.type : 'other',
  };
}
